import * as arrow from "apache-arrow";
import { Box } from "@mui/material";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useWasm } from "../../../contexts/app/provider";

import { DatePivotParams } from "../../../entities/date_pivot_params";
import { ErrorState } from "./ErrorState";
import { LoadingState } from "./LoadingState";
import { paramsToTransformArgs as defaultParamsToTransformArgs } from "../../../utils/params_to_transform_args";
import {
  parseParquetData,
  parseParquetDataWithRefresh,
  ParsedParquetData,
  FullParsedParquetData,
  ChunkedParsedParquetData,
} from "../../../utils/parquet";
import { useChunkedParquetRowProvider } from "../../../utils/chunked_parquet_provider";
import { PivotViewConfig } from "../../../entities/pivot_view_config";
import { ChartConfigProp } from "../../../entities/chart_config";
import { UniversalTableViewer, TableType } from "../../universal_mui_table";
import { UniversalChartViewer } from "../../universal_mui_chart";
import { usePerspective } from "../../../contexts/app/provider";
import {
  GetTransformedParquetDownloadUrlDocument,
  GetExportCsvDownloadUrlDocument,
  GetMatrixSortKeysDocument,
  RunSettings,
  useGetTransformedRawParquetSubscription,
} from "../../../services/perspective_api/__generated__/perspective_server-types";
import { AuthenticationService } from "../../../services/perspective_api/authentication";
import type { BackendExportFn } from "../index";

function ChunkedTableViewer<T extends DatePivotParams = DatePivotParams>({
  chunkedConfig,
  params,
  viewControllerConfig,
  tableType,
  columnHeaderMap,
  columnFormatters,
  sortConfig,
  setSortConfig,
  columnSchema,
  expandedGroups,
  setExpandedGroups,
  TableViewer,
  fetchSortKeys,
}: {
  chunkedConfig: {
    downloadUrl: string;
    parquetRowCount: number;
    getRefreshUrl: () => Promise<string | null>;
  };
  params: T;
  viewControllerConfig: PivotViewConfig<T>;
  tableType?: string;
  columnHeaderMap?: Record<string, string>;
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  columnSchema: Record<
    string,
    {
      dtype: string;
      min?: string;
      max?: string;
      unique_values?: string[];
      value_counts?: Record<string, number>;
    }
  >;
  expandedGroups?: Set<string>;
  setExpandedGroups?: React.Dispatch<React.SetStateAction<Set<string>>>;
  TableViewer: React.ComponentType<any>;
  fetchSortKeys?: (measure: string) => Promise<FullParsedParquetData | null>;
}) {
  const { rowCount, getRow, columns, isColumnsReady } =
    useChunkedParquetRowProvider({
      downloadUrl: chunkedConfig.downloadUrl,
      totalRowCount: chunkedConfig.parquetRowCount,
      getRefreshUrl: chunkedConfig.getRefreshUrl,
    });

  if (!isColumnsReady || !columns || columns.length === 0) {
    return <LoadingState />;
  }

  const chunkedParsedData: ParsedParquetData = {
    chunked: true,
    rowCount,
    getRow,
    columns,
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableViewer
        parsedData={chunkedParsedData}
        loading={false}
        error={null}
        viewParams={params}
        viewControllerConfig={viewControllerConfig}
        tableType={tableType as TableType}
        columnHeaderMap={columnHeaderMap}
        columnFormatters={columnFormatters}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
        columnSchema={columnSchema}
        expandedGroups={expandedGroups}
        setExpandedGroups={setExpandedGroups}
        fetchSortKeys={fetchSortKeys}
      />
    </Box>
  );
}

export const TransformedCustomViewViewerContainer = <
  T extends DatePivotParams = DatePivotParams,
>({
  params,
  runSettings,
  comparedToRunSettings,
  viewControllerConfig,
  viewerComponent: ViewerComponent,
  tableType,
  columnHeaderMap,
  columnFormatters,
  parsedData,
  setParsedData,
  isChartView,
  sortConfig,
  setSortConfig,
  chartConfig,
  ChartComponent,
  columnSchema,
  expandedGroups,
  setExpandedGroups,
  setBackendExportFn,
  setInMemoryArrowTable,
}: {
  params: T;
  runSettings: RunSettings | null;
  comparedToRunSettings: RunSettings | null;
  viewControllerConfig: PivotViewConfig<T>;
  viewerComponent: React.ComponentType<{
    parsedData: ParsedParquetData | null;
    loading: boolean;
    error: string | null;
    viewParams: T;
    tableType?: string;
    columnHeaderMap?: Record<string, string>;
    columnFormatters?: Record<
      string,
      (value: any, row: any) => React.ReactNode
    >;
    columnSchema?: Record<
      string,
      {
        dtype: string;
        min?: string;
        max?: string;
        unique_values?: string[];
        value_counts?: Record<string, number>;
      }
    >;
  }> | null;
  tableType?: string;
  columnHeaderMap?: Record<string, string>;
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>;
  parsedData: ParsedParquetData | null;
  setParsedData: (data: ParsedParquetData | null) => void;
  isChartView?: boolean;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  chartConfig: ChartConfigProp;
  ChartComponent?: React.ComponentType<any>;
  columnSchema: Record<
    string,
    {
      dtype: string;
      min?: string;
      max?: string;
      unique_values?: string[];
      value_counts?: Record<string, number>;
    }
  >;
  expandedGroups?: Set<string>;
  setExpandedGroups?: React.Dispatch<React.SetStateAction<Set<string>>>;
  setBackendExportFn?: React.Dispatch<React.SetStateAction<BackendExportFn | null>>;
  setInMemoryArrowTable?: (table: arrow.Table | null) => void;
}) => {
  const { datasetName, transformId, folder, paramsToTransformArgs } =
    viewControllerConfig;
  const chunkedMatrixTransformId =
    (viewControllerConfig as { chunkedMatrixTransformId?: string })
      ?.chunkedMatrixTransformId;
  const { perspectiveApiService } = usePerspective();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [chunkedConfig, setChunkedConfig] = useState<{
    downloadUrl: string;
    parquetRowCount: number;
    getRefreshUrl: () => Promise<string | null>;
  } | null>(null);
  const { wasmInitialized, wasmError } = useWasm();
  const [isSubscriptionSkipped, setIsSubscriptionSkipped] = useState(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();
  const inMemoryMatrixTableRef = useRef<arrow.Table | null>(null);

  const computeArgsJson = useCallback(() => {
    return paramsToTransformArgs
      ? paramsToTransformArgs(params)
      : defaultParamsToTransformArgs(params);
  }, [paramsToTransformArgs, params]);

  const argsJson = useMemo(() => computeArgsJson(), [computeArgsJson]);

  /** Use matrix transform from the start when configured, so we don't wait for the long-format transform first. */
  const effectiveTransformId =
    chunkedMatrixTransformId ?? transformId;

  const subscriptionVariables = useMemo(() => {
    const vars = {
      datasetName,
      transformId: effectiveTransformId,
      folder,
      runSettingsJson: runSettings ?? null,
      comparedToRunSettingsJson: comparedToRunSettings ?? null,
      format: "parquet" as const,
      params: argsJson,
    };
    return vars;
  }, [
    datasetName,
    effectiveTransformId,
    folder,
    runSettings,
    comparedToRunSettings,
    argsJson,
  ]);

  // Always use UniversalTableViewer as the default viewerComponent if no viewerComponent is provided
  const TableViewer = ViewerComponent || UniversalTableViewer;
  // Same for chart viewer
  const ChartViewer = ChartComponent || UniversalChartViewer;

  // Cleanup effect for retry timeout
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Listen for token refresh events to clear subscription skip state
  useEffect(() => {
    const handleTokenRefresh = () => {
      console.info("Token refreshed, clearing subscription skip state");
      setIsSubscriptionSkipped(false);
    };

    AuthenticationService.setOnTokenRefresh(handleTokenRefresh);

    return () => {
      // Clean up the callback when component unmounts
      AuthenticationService.setOnTokenRefresh(() => {});
    };
  }, []);

  // Force subscription restart when run changes
  const [subscriptionKey, setSubscriptionKey] = useState(0);

  useEffect(() => {
    setSubscriptionKey((prev) => prev + 1);
  }, [runSettings?.runAttemptId]);

  // Use subscription key to force restart
  const subscriptionVariablesWithKey = useMemo(
    () => ({
      ...subscriptionVariables,
      _subscriptionKey: subscriptionKey, // Add key to force subscription restart
    }),
    [subscriptionVariables, subscriptionKey]
  );

  useGetTransformedRawParquetSubscription({
    client: perspectiveApiService.getClient(),
    variables: subscriptionVariablesWithKey,
    onData: (data: any) => {
      const status = data.data?.data?.getTransformedRawParquet.status;
      const parquetDataString =
        data.data?.data?.getTransformedRawParquet.parquetData;
      const errorMessage =
        data.data?.data?.getTransformedRawParquet.errorMessage;

      if (status === "COMPLETED" && parquetDataString) {
        const isBase64 = !parquetDataString.startsWith("http");
        const parquetRowCount =
          data.data?.data?.getTransformedRawParquet?.parquetRowCount ?? null;
        setError(null);

        const refreshVariables = {
          datasetName,
          transformId: effectiveTransformId,
          folder,
          runSettingsJson: runSettings ?? null,
          comparedToRunSettingsJson: comparedToRunSettings ?? null,
          params: argsJson,
        };
        const getRefreshUrl = async () => {
          const result = await perspectiveApiService
            .getClient()
            .query({
              query: GetTransformedParquetDownloadUrlDocument,
              variables: refreshVariables,
            });
          return result.data?.getTransformedParquetDownloadUrl?.downloadUrl ?? null;
        };

        if (!isBase64 && parquetRowCount != null && parquetRowCount > 0 && chunkedMatrixTransformId != null) {
          console.log(
            "[transformed view] using chunked parquet: URL + rowCount =",
            parquetRowCount.toLocaleString()
          );
          inMemoryMatrixTableRef.current = null;
          setInMemoryArrowTable?.(null);
          setChunkedConfig({
            downloadUrl: parquetDataString,
            parquetRowCount,
            getRefreshUrl,
          });
          setParsedData(null);
          setLoading(false);
        } else {
          console.log(
            "[transformed view] using in-memory parquet (chunked path not used: server sent",
            isBase64 ? "base64" : "URL without parquetRowCount",
            ")"
          );
          setChunkedConfig(null);
          setLoading(true);
          const parsePromise = isBase64
            ? parseParquetData(parquetDataString, true)
            : parseParquetDataWithRefresh(parquetDataString, getRefreshUrl);

          parsePromise
            .then((parsed) => {
              if (chunkedMatrixTransformId && "arrowTable" in parsed) {
                // Matrix transform returned base64 (small dataset). Wrap the
                // in-memory arrow table as ChunkedParsedParquetData so the
                // matrix viewer in UniversalTableViewer handles it correctly.
                const full = parsed as FullParsedParquetData;
                inMemoryMatrixTableRef.current = full.arrowTable;
                setInMemoryArrowTable?.(full.arrowTable);
                const wrapped: ChunkedParsedParquetData = {
                  chunked: true,
                  rowCount: full.arrowTable.numRows,
                  getRow: (idx: number) => {
                    const row: Record<string, unknown> = {};
                    for (const field of full.arrowTable.schema.fields) {
                      row[field.name] = full.arrowTable.getChild(field.name)?.get(idx);
                    }
                    return row;
                  },
                  columns: full.columns,
                };
                console.log(
                  "[transformed view] matrix base64 → wrapped as chunked, rows:",
                  full.arrowTable.numRows
                );
                setParsedData(wrapped);
              } else {
                inMemoryMatrixTableRef.current = null;
                setInMemoryArrowTable?.(null);
                setParsedData(parsed);
              }
              setLoading(false);
            })
            .catch((err) => {
              console.error("[transformed view] parse failed:", err);
              inMemoryMatrixTableRef.current = null;
              setInMemoryArrowTable?.(null);
              setError(err instanceof Error ? err.message : String(err));
              setLoading(false);
              setParsedData(null);
            });
        }
      } else if (status === "LOADING") {
        inMemoryMatrixTableRef.current = null;
        setInMemoryArrowTable?.(null);
        setLoading(true);
        setError(null);
        setParsedData(null);
        setChunkedConfig(null);
      } else if (status === "FAILED" || errorMessage) {
        console.error(
          "[transformed view] transformation failed:",
          { status, errorMessage, transformId: effectiveTransformId }
        );
        inMemoryMatrixTableRef.current = null;
        setInMemoryArrowTable?.(null);
        setLoading(false);
        setError(errorMessage || "Transformation failed");
        setParsedData(null);
        setChunkedConfig(null);
      }
    },
    onError: (error: any) => {
      // Check if this is a network error (handled gracefully by authentication service)
      const isNetworkError = error?.message?.includes("Network unavailable");

      if (isNetworkError) {
        // Network errors are handled gracefully by authentication service
        // Just show loading state and let the service handle retries
        console.warn(
          "TransformedCustomViewViewerContainer: Network error, will retry automatically"
        );
        setIsSubscriptionSkipped(true);
        retryTimeoutRef.current = setTimeout(() => {
          setIsSubscriptionSkipped(false);
        }, 5000); // 5 seconds for network errors
      } else {
        // For other errors, log and use shorter retry delay
        console.error("TransformedCustomViewViewerContainer: error", error);
        setIsSubscriptionSkipped(true);
        retryTimeoutRef.current = setTimeout(() => {
          setIsSubscriptionSkipped(false);
        }, 3000);
      }
    },
    skip: isSubscriptionSkipped || !wasmInitialized,
  });

  // Reset state when parameters change
  useEffect(() => {
    inMemoryMatrixTableRef.current = null;
    setInMemoryArrowTable?.(null);
    setParsedData(null);
    setChunkedConfig(null);
    setLoading(true);
    setError(null);
  }, [
    datasetName,
    transformId,
    folder,
    runSettings,
    comparedToRunSettings,
    argsJson,
    setParsedData,
    setInMemoryArrowTable,
  ]);

  // Extract sort keys from an in-memory arrow table by filtering rows for
  // the requested measure. Used when the matrix data arrived as base64
  // (small dataset) and the backend S3 cache may not be available.
  const extractLocalSortKeys = useCallback(
    (table: arrow.Table, measure: string): FullParsedParquetData | null => {
      const measureCol = table.getChild("measure");
      if (!measureCol) return null;

      const measureLower = measure.toLowerCase();
      const matchingIndices: number[] = [];
      for (let i = 0; i < table.numRows; i++) {
        if (String(measureCol.get(i)).toLowerCase() === measureLower) {
          matchingIndices.push(i);
        }
      }
      if (matchingIndices.length === 0) return null;

      const data: Record<string, unknown[]> = {};
      for (const field of table.schema.fields) {
        const col = table.getChild(field.name);
        if (col) {
          data[field.name] = matchingIndices.map((i) => col.get(i));
        }
      }
      const filtered = arrow.tableFromArrays(data);
      return { arrowTable: filtered, columns: filtered.schema.fields };
    },
    []
  );

  // Build fetchSortKeys callback for the matrix path.
  // When the full data is in memory (base64), filters locally.
  // Otherwise queries the backend for a small parquet with only the
  // requested measure's rows.
  const fetchSortKeys = useCallback(
    async (measure: string): Promise<FullParsedParquetData | null> => {
      // Fast path: extract from in-memory arrow table (small dataset)
      const localTable = inMemoryMatrixTableRef.current;
      if (localTable) {
        const local = extractLocalSortKeys(localTable, measure);
        if (local) {
          console.log("[fetchSortKeys] extracted locally, rows:", local.arrowTable.numRows);
          return local;
        }
      }

      // Slow path: query the backend (large chunked dataset)
      try {
        const result = await perspectiveApiService.getClient().query({
          query: GetMatrixSortKeysDocument,
          variables: {
            datasetName,
            transformId: effectiveTransformId,
            folder,
            runSettingsJson: runSettings ?? null,
            comparedToRunSettingsJson: comparedToRunSettings ?? null,
            params: argsJson,
            measure,
          },
          fetchPolicy: "no-cache",
        });

        const b64 = result.data?.getMatrixSortKeys;
        if (!b64) {
          console.warn("[fetchSortKeys] Backend returned null for measure:", measure);
          return null;
        }

        const parsed = await parseParquetData(b64, true);
        return parsed as FullParsedParquetData;
      } catch (err) {
        console.error("[fetchSortKeys] Error:", err);
        return null;
      }
    },
    [
      extractLocalSortKeys,
      perspectiveApiService,
      datasetName,
      effectiveTransformId,
      folder,
      runSettings,
      comparedToRunSettings,
      argsJson,
    ]
  );

  // Build backend CSV export callback.  Available whenever the view uses a
  // chunkedMatrixTransformId (inventory health views) -- even when the data
  // arrived as base64 (small dataset).  The backend reads the cached parquet
  // from S3, applies sorting, and returns a signed CSV download URL.
  const backendExport: BackendExportFn | null = useMemo(() => {
    if (!chunkedConfig && !chunkedMatrixTransformId) return null;
    return async (excludeDetailRows: boolean) => {
      const sortConfigJson =
        sortConfig.length > 0
          ? sortConfig.map(({ key, direction }) => ({ key, direction }))
          : null;
      const firstSort = sortConfig[0];
      const result = await perspectiveApiService.getClient().query({
        query: GetExportCsvDownloadUrlDocument,
        variables: {
          datasetName,
          transformId: effectiveTransformId,
          folder,
          runSettingsJson: runSettings ?? null,
          comparedToRunSettingsJson: comparedToRunSettings ?? null,
          params: argsJson,
          measure: params?.measure ?? null,
          excludeDetailRows,
          sortColumn: firstSort?.key ?? null,
          sortDirection: firstSort?.direction ?? null,
          sortConfig: sortConfigJson,
        },
        fetchPolicy: "no-cache",
      });

      const downloadUrl =
        result.data?.getExportCsvDownloadUrl?.downloadUrl;
      if (!downloadUrl) {
        throw new Error("Backend did not return a download URL for CSV export");
      }

      // Trigger browser download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
  }, [
    chunkedConfig,
    chunkedMatrixTransformId,
    sortConfig,
    perspectiveApiService,
    datasetName,
    effectiveTransformId,
    folder,
    runSettings,
    comparedToRunSettings,
    argsJson,
    params?.measure,
  ]);

  // Register/unregister the backend export callback with the parent
  useEffect(() => {
    if (setBackendExportFn) {
      setBackendExportFn(() => backendExport);
    }
    return () => {
      if (setBackendExportFn) {
        setBackendExportFn(() => null);
      }
    };
  }, [backendExport, setBackendExportFn]);

  // Show WASM error if initialization failed
  if (wasmError) {
    return <ErrorState error={wasmError} />;
  }

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (chunkedConfig && params) {
    return (
      <ChunkedTableViewer
        chunkedConfig={chunkedConfig}
        params={params}
        viewControllerConfig={viewControllerConfig}
        tableType={tableType}
        columnHeaderMap={columnHeaderMap}
        columnFormatters={columnFormatters}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
        columnSchema={columnSchema}
        expandedGroups={expandedGroups}
        setExpandedGroups={setExpandedGroups}
        TableViewer={TableViewer}
        fetchSortKeys={fetchSortKeys}
      />
    );
  }

  if (!parsedData || !params) {
    return <ErrorState error="No data available" />;
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isChartView && chartConfig && typeof chartConfig === "function" ? (
        <ChartViewer<T>
          parsedData={parsedData}
          viewParams={params}
          chartConfig={chartConfig(params)}
        />
      ) : (
        <TableViewer<T>
          parsedData={parsedData}
          loading={loading}
          error={error}
          viewParams={params}
          viewControllerConfig={viewControllerConfig}
          tableType={tableType as TableType}
          columnHeaderMap={columnHeaderMap}
          columnFormatters={columnFormatters}
          sortConfig={sortConfig}
          setSortConfig={setSortConfig}
          columnSchema={columnSchema}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
          fetchSortKeys={fetchSortKeys}
        />
      )}
    </Box>
  );
};
